import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AircraftTitle from '../components/aircraft/AircraftTitle';
import AircraftViewer from '../components/aircraft/AircraftViewer';
import ComponentOverlay from '../components/aircraft/ComponentOverlay';
import ManufacturerCard from '../components/aircraft/ManufacturerCard';
import EngineCard from '../components/aircraft/EngineCard';
import AvionicsCard from '../components/aircraft/AvionicsCard';
import LargestFleetCard from '../components/aircraft/LargestFleetCard';
import SpecsCard from '../components/aircraft/SpecsCard';
import AdditionalInfo from '../components/aircraft/AdditionalInfo';

// Import aircraft data
import boeing737Data from '../data/aircraft/commercial/boeing-737.json';
import airbusA320Data from '../data/aircraft/commercial/airbus-a320.json';
import boeing787Data from '../data/aircraft/commercial/boeing-787.json';
import f22RaptorData from '../data/aircraft/military/f-22-raptor.json';
import halTejasData from '../data/aircraft/military/hal-tejas.json';
import dassaultRafaleData from '../data/aircraft/military/dassault-rafale.json';
import f117NighthawkData from '../data/aircraft/military/f-117-nighthawk.json';
import v22OspreyData from '../data/aircraft/military/v-22-osprey.json';
import su30Data from '../data/aircraft/military/su-30.json';

// Aircraft data mapping
const aircraftDatabase = {
  'boeing-737-800': boeing737Data,
  'airbus-a320': airbusA320Data,
  'boeing-787-9': boeing787Data,
  'f-22-raptor': f22RaptorData,
  'hal-tejas': halTejasData,
  'dassault-rafale': dassaultRafaleData,
  'f-117-nighthawk': f117NighthawkData,
  'v-22-osprey': v22OspreyData,
  'su-30': su30Data
};

function AircraftDetailPage() {
  const { id } = useParams();
  const [selectedComponent, setSelectedComponent] = useState(null);

  // Load aircraft data dynamically based on ID
  const aircraftData = aircraftDatabase[id];

  if (!aircraftData) {
    return (
      <div className="not-found">
        <h1>Aircraft not found</h1>
        <Link to="/">← Back to Explore</Link>
      </div>
    );
  }

  return (
    <div className="aircraft-detail-page">
      {/* Back to explore link */}
      <div className="back-nav">
        <Link to="/" className="back-link">← Back to Explore</Link>
      </div>

      {/* Main content */}
      <main className="main-content">
        {/* Aircraft title */}
        <AircraftTitle
          model={aircraftData.model}
          subtitle={aircraftData.subtitle}
        />

        {/* Left column: 3D Model + content below */}
        <div className="left-column">
          {/* Merged viewer and about section */}
          <div className="model-about-merged">
            <div className="model-viewport">
              <AircraftViewer
                modelFile={aircraftData.modelFile}
                components={aircraftData.components}
                onComponentHover={setSelectedComponent}
                debugMode={aircraftData.debug || false}
              />
              <ComponentOverlay component={selectedComponent} />
            </div>

            {/* Additional content below model */}
            <AdditionalInfo
              about={aircraftData.about}
            />
          </div>

          {/* Specifications card */}
          <SpecsCard
            specs={aircraftData.specs}
          />
        </div>

        {/* Right: Content area */}
        <div className="content-area">
          <ManufacturerCard
            manufacturer={aircraftData.manufacturer}
            manufacturers={aircraftData.manufacturers}
            firstFlight={aircraftData.overview?.firstFlight}
            unitBuilt={aircraftData.overview?.unitBuilt}
            cost={aircraftData.overview?.cost}
          />

          {aircraftData.engine && (
            <EngineCard
              engineFamily={aircraftData.engine.family}
              variants={aircraftData.engine.variants}
              thrust={aircraftData.engine.thrust}
              bypassRatio={aircraftData.engine.bypassRatio}
              pressureRatio={aircraftData.engine.pressureRatio}
              logoPath={aircraftData.engine.logoPath}
              suppliers={aircraftData.engine.suppliers}
            />
          )}

          {aircraftData.avionics && (
            <AvionicsCard
              supplier={aircraftData.avionics.supplier}
              logoPath={aircraftData.avionics.logoPath}
              suppliers={aircraftData.avionics.suppliers}
              stats={aircraftData.avionics.stats}
            />
          )}

          {aircraftData.largestFleet && (
            <LargestFleetCard
              airline={aircraftData.largestFleet.airline}
              fleetSize={aircraftData.largestFleet.fleetSize}
              logoPath={aircraftData.largestFleet.logoPath}
              stats={aircraftData.largestFleet.stats}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default AircraftDetailPage;
